import { useEffect, useMemo, useState } from "react";
import ModalShell from "./ModalShell";
import WfEmployeeSelect from "./WfEmployeeSelect";
import WfShiftSelect from "./WfShiftSelect";
import ShiftPreviewCard from "./ShiftPreviewCard";
import { validateAssignment } from "../../Moduels/attendance/rosterValidation";
import { normalizeEmployeeId, normalizeShiftId } from "../../Moduels/attendance/rosterAssignmentUtils";
import DatePickerField from "../common/DatePickerField";
import { rosterTheme as t } from "./rosterTheme";

export default function AssignmentEditModal({
  open,
  onClose,
  assignment,
  roster,
  shifts,
  existing,
  readOnly,
  requireOverride,
  onSave,
}) {
  const [form, setForm] = useState({
    employee_id: "",
    work_date: "",
    shift_id: "",
    notes: "",
    override_reason: "",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (assignment) {
      setForm({
        employee_id: assignment.employee_id || "",
        work_date: assignment.work_date || "",
        shift_id: assignment.shift_id || "",
        notes: assignment.notes || "",
        override_reason: "",
      });
      setErrors({});
    }
  }, [assignment]);

  const selectedShift = useMemo(
    () => (shifts || []).find((s) => s.shift_id === form.shift_id),
    [shifts, form.shift_id]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validateAssignment(
      { ...form, override_required: requireOverride },
      roster,
      existing || [],
      assignment?.assignment_id
    );
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell open={open} title={assignment ? "Edit assignment" : "New assignment"} onClose={onClose} wide>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className={t.label}>Employee</label>
          <WfEmployeeSelect
            value={form.employee_id}
            onChange={(id) => setForm({ ...form, employee_id: normalizeEmployeeId(id) })}
            disabled={readOnly}
          />
          {errors.employee_id && <p className="text-xs text-red-600">{errors.employee_id}</p>}
        </div>
        <DatePickerField
          label="Work date"
          value={form.work_date}
          min={roster?.period_start}
          max={roster?.period_end}
          inputClassName={t.input}
          onChange={(date) => setForm({ ...form, work_date: date })}
          disabled={readOnly}
        />
        {errors.work_date && <p className="text-xs text-red-600">{errors.work_date}</p>}
        <div>
          <label className={t.label}>Shift</label>
          <WfShiftSelect
            shifts={shifts}
            value={form.shift_id}
            onChange={(id) => setForm({ ...form, shift_id: normalizeShiftId(id) })}
            disabled={readOnly}
          />
          {errors.shift_id && <p className="text-xs text-red-600">{errors.shift_id}</p>}
        </div>
        <ShiftPreviewCard shift={selectedShift} />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            className="w-full border rounded-lg px-3 py-2 text-sm"
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            disabled={readOnly}
          />
        </div>
        {requireOverride && (
          <div>
            <label className="block text-sm font-medium text-amber-800 mb-1">Override reason *</label>
            <textarea
              className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm"
              rows={2}
              value={form.override_reason}
              onChange={(e) => setForm({ ...form, override_reason: e.target.value })}
            />
            {errors.override_reason && (
              <p className="text-xs text-red-600">{errors.override_reason}</p>
            )}
          </div>
        )}
        {!readOnly && (
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className={t.btnSecondary}>
              Cancel
            </button>
            <button type="submit" disabled={saving} className={t.btnPrimary}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </form>
    </ModalShell>
  );
}
